<template>
  <div>
    <McLoading :isLoading="isSubmitting" />
    <b-modal
      id="modal-change-sequence"
      class="mc-modal"
      :title="title"
      header-bg-variant="primary"
      size="md"
      centered
      no-close-on-esc
      no-close-on-backdrop
    >
      <McAlert
        v-if="alertMessage"
        variant="warning"
        :html="alertMessage"
        withIcon
        :withGap="false"
      />
      <p class="mt-2 p-0">
        {{ message }}
      </p>
      <template #modal-footer>
        <McButton
          variant="secondary"
          class="me-2"
          :text="$t('global.back')"
          :disabled="isSubmitting"
          @click="$bvModal.hide('modal-change-sequence')"
        />
        <McButton
          variant="primary"
          type="submit"
          :text="buttonText"
          :disabled="isSubmitting"
          @click="handleMovement"
        />
      </template>
    </b-modal>

    <ErrorMovement />
  </div>
</template>
<script>
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import ErrorMovement from './ErrorMovement'
import * as RO from '@/services/rest-api/lib/route-optimization'
import { errorCodeToastMap } from '@/router/views/route-optimization/lib/constants/error-code-mappings'

export default {
  components: {
    McButton,
    McAlert,
    ErrorMovement,
  },
  props: {
    from: {
      type: Object,
      default: () => ({}),
    },
    to: {
      type: Object,
      default: () => ({}),
    },
    position: {
      type: String,
      default: 'AFTER',
    },
    sameVehicle: {
      type: Boolean,
      default: false,
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    addedLast: {
      type: Boolean,
      default: false,
    },
    timeWindows: {
      type: Boolean,
      default: false,
    },
    isRoSwatActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      quotaStatus: 'available',
      isSubmitting: false,
    }
  },
  computed: {
    id() {
      return this.$route.params.id
    },
    title() {
      if (!this.sameVehicle) {
        return this.$t('routeOptimization.alert.changeSequenceOtherVehicleTitle')
      }
      return this.$t('routeOptimization.alert.changeSequenceTitle')
    },
    message() {
      if (!this.sameVehicle) {
        return this.$t('routeOptimization.alert.changeSequenceOtherVehicleMessage', {
          location: this.from?.point?.address?.name,
          vehicle: this.to?.targetVehicle?.license_plate,
          sequence: this.to?.targetIndex,
        })
      }
      return this.$t('routeOptimization.alert.changeSequenceMessage', {
        location: this.from?.point?.address?.name,
        sequence: this.to?.targetIndex,
      })
    },
    buttonText() {
      if (!this.sameVehicle) {
        return this.$t('routeOptimization.action.movedToOtherVehicle')
      }
      return this.$t('routeOptimization.action.changeSequence')
    },
    alertMessage() {
      const options = this.settings?.options

      if (this.timeWindows) {
        return this.$t('routeOptimization.alert.timeWindows')
      }

      if ((options?.volume_capacity || options?.weight_capacity) && !this.sameVehicle) {
        return this.$t('routeOptimization.alert.overCapacity')
      }

      return false
    },
  },
  methods: {
    async handleMovement() {
      if (this.isSubmitting) return
      this.isSubmitting = true
      this.$bvModal.hide('modal-change-sequence')

      try {
        const rov_id = this.from.vehicle.id
        const rop_id = this.from.point.point_id
        const data = {
          route_optimization_vehicle_id: this.to.targetVehicle.id,
          movement: {
            type: this.position,
            route_optimization_point_id: this.to.targetPoint.point_id,
          },
        }

        if (this.isRoSwatActive) {
          data.engine = this.settings?.engine
        }

        const res = await RO.movement(this.id, rov_id, rop_id, data)

        if (res.status === 500) {
          this.$bvModal.hide('modal-change-sequence')

          this.$bvModal.show('modal-error-movement')
          return
        }

        let errorMessage = this.$t('routeOptimization.alert.failedChangeSequence')

        if (res.errors_v2) {
          const issue = res.errors_v2.issues.find((issue) => errorCodeToastMap[issue.code])
          if (issue) {
            errorMessage = this.$t(errorCodeToastMap[issue.code])
            if (issue.code === 'FLEET_TASK.ROUTE_OPTIMIZATION_LIMIT_REACHED') {
              this.quotaStatus = 'empty'
              this.$mcToast.error(errorMessage)
              this.$bvModal.hide('modal-change-sequence')
              this.$emit('quotaStatus', this.quotaStatus)
              return
            }
          }
          this.$mcToast.error(errorMessage)
          this.$bvModal.hide('modal-change-sequence')
          this.quotaStatus = 'available'
          this.$emit('quotaStatus', this.quotaStatus)
          return
        }

        this.$bvModal.hide('modal-change-sequence')
        if (this.sameVehicle) {
          this.$mcToast.success(this.$t('routeOptimization.alert.successChangeSequence'))
        } else {
          this.$mcToast.success(
            this.$t('routeOptimization.alert.successChangeSequenceOtherVehicle', {
              vehicle: this.to.targetVehicle.license_plate,
            })
          )
        }
        await this.$nextTick()
        this.$router.push({ name: 'Route Optimization Detail', params: { id: res.data.id } })
        this.$emit('quotaStatus', this.quotaStatus)
      } catch (error) {
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>
